import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Card } from '../components/Card'
import { InfoIcon, LearnTip } from '../lib/learn-mode'
import { useTranslation } from 'react-i18next'

interface Scenario {
  id: string
  name: string
  ships: number       // baseline ships in period (last 90d from DB)
  sellPrice: number   // avg sell price per ship
  cost: number        // avg carrier cost per ship
  discountUsd: number // dollars off (user-controlled slider default)
  volumeIncreasePct: number // percent increase (user-controlled slider default)
}

// Slider defaults per scenario (user inputs — not from DB)
const SCENARIO_SLIDER_DEFAULTS: Record<string, { discountUsd: number; volumeIncreasePct: number }> = {
  'usps-ga':  { discountUsd: 0.20, volumeIncreasePct: 15 },
  'dhl-ex':   { discountUsd: 0.30, volumeIncreasePct: 10 },
  'fedex-ge': { discountUsd: 0.40, volumeIncreasePct: 20 },
  'ups-grd':  { discountUsd: 0.50, volumeIncreasePct: 30 },
}

// Fallback used only while the API fetch is loading
const FALLBACK_SCENARIOS: Scenario[] = [
  { id: 'usps-ga',  name: 'USPS - Ground Advantage', ships: 0, sellPrice: 0, cost: 0, discountUsd: 0.20, volumeIncreasePct: 15 },
  { id: 'dhl-ex',   name: 'DHL - Express',            ships: 0, sellPrice: 0, cost: 0, discountUsd: 0.30, volumeIncreasePct: 10 },
  { id: 'fedex-ge', name: 'FedEx - Ground Economy',   ships: 0, sellPrice: 0, cost: 0, discountUsd: 0.40, volumeIncreasePct: 20 },
  { id: 'ups-grd',  name: 'UPS - Ground',             ships: 0, sellPrice: 0, cost: 0, discountUsd: 0.50, volumeIncreasePct: 30 },
]

function compute(s: Scenario) {
  const margin = s.sellPrice - s.cost
  const newMargin = margin - s.discountUsd
  const requiredVolumeMultiplier = newMargin > 0 ? (margin / newMargin - 1) : Infinity
  const newShips = s.ships * (1 + s.volumeIncreasePct / 100)
  const newProfit = newShips * newMargin
  const oldProfit = s.ships * margin
  const delta = newProfit - oldProfit
  return { margin, newMargin, requiredVolumeMultiplier, newShips, newProfit, oldProfit, delta }
}

export function PricingView() {
  const { t } = useTranslation()
  // Fetch live baselines (last 90d per carrier) — falls back to 0s while loading
  const { data: apiBaselines } = useQuery<Array<{ id: string; name: string; ships: number; sellPrice: number; cost: number }>>({
    queryKey: ['pricing', 'baselines'],
    queryFn: () => fetch('/api/pricing/baselines').then(r => r.json()),
    staleTime: 5 * 60_000,
  })

  // Merge DB baselines with slider defaults
  const scenarios: Scenario[] = (apiBaselines ?? FALLBACK_SCENARIOS).map(b => ({
    ...b,
    discountUsd:       SCENARIO_SLIDER_DEFAULTS[b.id]?.discountUsd       ?? 0.20,
    volumeIncreasePct: SCENARIO_SLIDER_DEFAULTS[b.id]?.volumeIncreasePct ?? 10,
  }))

  const [scope, setScope] = useState<string>(scenarios[0]?.id ?? 'usps-ga')
  const current = scenarios.find(s => s.id === scope) ?? scenarios[0]
  const [discount, setDiscount] = useState<number>(current?.discountUsd ?? 0.20)
  const [volIncrease, setVolIncrease] = useState<number>(current?.volumeIncreasePct ?? 15)

  // Sync sliders when the user picks a different scope
  const handleScopeChange = (newId: string) => {
    setScope(newId)
    const next = scenarios.find(s => s.id === newId)
    if (next) { setDiscount(next.discountUsd); setVolIncrease(next.volumeIncreasePct) }
  }

  // Live scenario state (clones the base + sliders)
  const liveScenario: Scenario = { ...current, discountUsd: discount, volumeIncreasePct: volIncrease }
  const live = useMemo(() => compute(liveScenario), [liveScenario])

  // Pre-computed saved scenarios for the strip at the bottom
  const saved = scenarios.map(s => ({ ...s, calc: compute(s) }))

  return (
    <div className="space-y-5">
      <Card className="p-7">
        <div className="flex items-center justify-between gap-6">
          <div>
            <div className="text-[12px] font-semibold text-ink-500 uppercase tracking-wider">Pricing simulator</div>
            <div className="mt-2 text-[28px] font-bold text-ink-900 leading-none">
              {t('pricing.title')}
            </div>
            <div className="mt-2 text-[13px] text-ink-500">
              {t('pricing.subtitle')}
            </div>
            <LearnTip>
              {t('pricing.learn_tip')}
            </LearnTip>
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <div className="flex items-center gap-3 mb-5">
          <label className="text-[11px] font-semibold text-ink-500 uppercase tracking-wider">{t('pricing.scope')}</label>
          <select
            value={scope}
            onChange={e => handleScopeChange(e.target.value)}
            className="px-3 py-1.5 border border-ink-200 rounded-lg text-sm bg-white text-ink-700 font-medium focus-ring"
          >
            {scenarios.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
            <option disabled>───</option>
            <option disabled>Custom scope (coming soon)…</option>
          </select>
        </div>

        <div className="grid grid-cols-2 gap-8">
          {/* LEFT: baseline */}
          <div>
            <div className="text-[13px] font-semibold text-ink-800 mb-3">
              {t('pricing.baseline_90d')} <InfoIcon tip={t('pricing.baseline_90d_tip')} />
            </div>
            <div className="space-y-2 text-[13px]">
              {[
                [t('pricing.ships_in_period'), current.ships.toLocaleString()],
                [t('pricing.avg_sell_price'), `$${current.sellPrice.toFixed(2)}`],
                [t('pricing.avg_carrier_cost'), `$${current.cost.toFixed(2)}`],
                [t('pricing.avg_margin'), `$${live.margin.toFixed(2)}  (${(live.margin / current.sellPrice * 100).toFixed(1)}%)`],
                [t('pricing.total_profit_period'), `$${live.oldProfit.toLocaleString(undefined, { maximumFractionDigits: 0 })}`],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between py-1.5 border-b border-dashed border-ink-200">
                  <span className="text-ink-500">{k}</span>
                  <span className="font-semibold text-ink-800">{v}</span>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT: scenario inputs + outcome */}
          <div>
            <div className="text-[13px] font-semibold text-ink-800 mb-3">{t('pricing.scenario')}</div>

            <div className="mb-4">
              <label className="block text-[11px] text-ink-500 mb-1 font-medium">
                {t('pricing.price_cut')} <span className="font-bold text-royal-600">${discount.toFixed(2)}</span> {t('pricing.per_ship')}
                ({(discount / current.sellPrice * 100).toFixed(1)}%)
              </label>
              <input
                type="range" min={0} max={1.5} step={0.01}
                value={discount}
                onChange={e => setDiscount(Number(e.target.value))}
                className="w-full"
              />
            </div>

            <div className="mb-4">
              <label className="block text-[11px] text-ink-500 mb-1 font-medium">
                {t('pricing.expected_volume_increase')} <span className="font-bold text-royal-600">+{volIncrease.toFixed(0)}%</span>
                <InfoIcon tip={t('pricing.expected_volume_tip')} />
              </label>
              <input
                type="range" min={0} max={200} step={1}
                value={volIncrease}
                onChange={e => setVolIncrease(Number(e.target.value))}
                className="w-full"
              />
            </div>

            <div className={`p-4 rounded-lg ${
              live.delta >= 0
                ? 'bg-mint-500 text-white'
                : live.newMargin <= 0
                  ? 'bg-flame-600 text-white'
                  : 'bg-flame-500 text-white'
            }`}>
              <div className="text-[20px] font-bold leading-tight">
                {live.newMargin <= 0
                  ? t('pricing.negative_margin')
                  : live.delta >= 0
                    ? `${t('pricing.gain')} $${Math.round(live.delta).toLocaleString()} ${t('pricing.over_period')}`
                    : `${t('pricing.lose')} $${Math.round(-live.delta).toLocaleString()} ${t('pricing.over_period')}`
                }
              </div>
              <div className="text-[12px] opacity-90 mt-1">
                {live.newMargin > 0 && (
                  <>
                    Break-even requires <b>+{(live.requiredVolumeMultiplier * 100).toFixed(1)}%</b> volume.
                    {live.delta >= 0
                      ? ` Your +${volIncrease.toFixed(0)}% bet covers it.`
                      : ` Your +${volIncrease.toFixed(0)}% bet doesn't reach it.`}
                  </>
                )}
              </div>
            </div>

            <div className="mt-3 text-[11px] text-ink-500 leading-relaxed">
              <InfoIcon tip={t('pricing.tool_tip')} />
              {t('pricing.math_disclaimer')}
            </div>
          </div>
        </div>
      </Card>

      {/* Saved scenarios strip */}
      <Card className="p-6">
        <div className="text-[13px] font-semibold text-ink-800 mb-3">
          {t('pricing.saved_scenarios')}
          <InfoIcon tip={t('pricing.saved_scenarios_tip')} />
        </div>
        <div className="grid grid-cols-4 gap-3">
          {saved.map(s => {
            const verdict = s.calc.newMargin <= 0 ? 'bad' : s.calc.delta >= 0 ? 'good' : 'warn'
            return (
              <div key={s.id} className="p-3.5 border border-ink-200 rounded-lg bg-white">
                <div className="text-[12px] font-bold text-royal-700 mb-1">{s.name}</div>
                <div className="text-[11px] text-ink-500">Margin: ${s.calc.margin.toFixed(2)} → ${s.calc.newMargin.toFixed(2)}</div>
                <div className="text-[11px] text-ink-500">
                  Need +{(s.calc.requiredVolumeMultiplier * 100).toFixed(1)}% · bet +{s.volumeIncreasePct}%
                </div>
                <div className={`mt-2 inline-block px-2 py-0.5 rounded text-[11px] font-semibold ${
                  verdict === 'good' ? 'pill-mint' : verdict === 'warn' ? 'pill-honey' : 'pill-flame'
                }`}>
                  {verdict === 'good'
                    ? `+$${Math.round(s.calc.delta)} / period`
                    : verdict === 'warn'
                      ? `gap`
                      : `-$${Math.round(-s.calc.delta)}`}
                </div>
              </div>
            )
          })}
        </div>
      </Card>
    </div>
  )
}
